import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Switch, Route, Redirect } from 'react-router-dom'
import { useQuery } from 'react-query'
import { initializeAnalytics } from '@offerzen/pinata-shame'
import { notify } from '@offerzen/react-utils'

import graphQLRequest from 'CanExConfig/graphQLRequest'

import Body from './components/Body'
import ScrollTop from './components/ScrollTop'
import Navbar, { navbarHeight } from './components/Navbar'
import PopulateInitialState from './components/PopulateInitialState'
import SystemErrorRedux from './components/SystemErrorRedux'
import LoadingScene from './scenes/LoadingScene'
import ErrorScene from './scenes/ErrorScene'
import AdminPanel from './components/AdminPanel'
import CurrentUser from './containers/CurrentUser'

import { initialiseCurrentUser } from './reducks/initialState'

import { getCurrentUser } from './apiModules/currentUser'
import { FETCH_CANDIDATE_PROFILE } from './graphql/candidateProfile'
import { errorUser as errorUserSelector } from './selectors/currentUser'

import HomeScene from './scenes/HomeScene'
import OnboardingMain from '../Onboarding/Main'
import SocialSignupComplete from '../Onboarding/scenes/SocialSignupComplete'
import InterviewRequestsMain from '../InterviewRequests/Main'
import DashboardMain from '../Dashboard/Main'
import ProfileMain from '../Profile/Main'
import JobsMain from '../Jobs/Main'
import ApplicationsMain from '../Applications/Main'

import { isOnboardingOnlyRoute } from './helpers/redirectHelpers'
import { identifyCandidate } from './helpers/analyticsHelpers'
import { notFoundReactQuery } from './helpers/errorHelpers'

import './styles'

const CandidateExperienceMain = () => {
  const dispatch = useDispatch()
  const errorUser = useSelector(state => errorUserSelector(state))

  const setCurrentUser = currentUser => {
    dispatch(initialiseCurrentUser(currentUser))
  }
  const {
    data: currentUser,
    status: userFetchStatus,
    refetch: refetchCurrentUser,
  } = useQuery(['currentUser'], () => getCurrentUser(), {
    refetchOnWindowFocus: false,
    onSuccess: data => {
      setCurrentUser(data)
    },
  })

  const isLoggedIn = Boolean(currentUser)
  const candidateProfileId = isLoggedIn ? currentUser.profile_id : undefined

  const {
    data,
    status: candidateProfileFetchStatus,
    error: candidateProfileFetchError,
  } = useQuery(['candidateProfile', candidateProfileId], () => graphQLRequest(FETCH_CANDIDATE_PROFILE, { id: candidateProfileId }), {
    refetchOnWindowFocus: false,
    enabled: isLoggedIn,
  })

  const isOnboarding = isLoggedIn && data && data.candidateProfile.currentState === 'ONBOARDING'

  const analyticsInitialised = useRef(false)
  useEffect(() => {
    if (!analyticsInitialised.current) {
      initializeAnalytics(process.env.REACT_APP_SEGMENT_WRITE_KEY)
      analyticsInitialised.current = true
    }
  }, [analyticsInitialised])

  useEffect(() => {
    if (data && currentUser) {
      identifyCandidate(currentUser, data.candidateProfile)
    }
  }, [data, currentUser])

  if (userFetchStatus === 'loading' || candidateProfileFetchStatus === 'loading') return <LoadingScene />

  if (userFetchStatus === 'error' || candidateProfileFetchStatus === 'error') {
    // For unknown reasons, sometimes the `GraphQLController` unexpectedly returns a 401
    // before the `Candidate::BaseController` is able to respond with a 404.
    // Unfortunately, not all GraphQL response errors share the same structure, e.g.
    // the 401 returned from `GraphQLController` does not match the structure returned
    // for the 404 from the `Candidate::BaseController`. This causes the `notFoundReactQuery`
    // helper to throw an error. In this case, we would rather display a generic error page
    // to the user and log the error in Sentry.
    let errorCode
    try {
      errorCode = notFoundReactQuery(candidateProfileFetchError) ? 404 : undefined
    } catch (err) {
      notify(err, errorUser)
    }

    return <ErrorScene errorCode={errorCode} />
  }

  const showAdminPanel = isLoggedIn && currentUser.category === 'admin'

  return (
    <Switch>
      <Route exact path="/">
        <HomeScene candidateProfileId={candidateProfileId} />
      </Route>

      {/* Social auth for sign in AND sign up */}
      <Route exact path="/candidate/social-auth" component={SocialSignupComplete} />

      <Choose>
        <When condition={!isLoggedIn || isOnboarding}>
          <Route
            path="/candidate"
            render={() => (
              <PopulateInitialState candidateProfileId={candidateProfileId}>
                <OnboardingMain refetchCurrentUser={refetchCurrentUser} />
                <If condition={showAdminPanel}>
                  <AdminPanel candidateProfileId={candidateProfileId} />
                </If>
              </PopulateInitialState>
            )}
          />
        </When>

        <Otherwise>
          <Route
            path="/candidate/:candidateSlug"
            render={routeProps => (
              <Choose>
                {/* Redirect onboarded users away from onboarding routes */}
                <When condition={isOnboardingOnlyRoute(routeProps.match.params.candidateSlug, routeProps.location.pathname)}>
                  <Redirect to={`/candidate/${candidateProfileId}/dashboard`} />
                </When>
                <Otherwise>
                  <PopulateInitialState candidateProfileId={routeProps.match.params.candidateSlug}>
                    <CurrentUser candidateSlug={routeProps.match.params.candidateSlug}>
                      {userProps => (
                        <Navbar
                          candidateSlug={routeProps.match.params.candidateSlug}
                          metadata={userProps.metadata}
                          trackVisibilityStatus={userProps.trackVisibilityStatus}
                          signOut={userProps.signOut}
                        />
                      )}
                    </CurrentUser>

                    <Body>
                      <ScrollTop location={routeProps.location}>
                        <SystemErrorRedux offsetTop={navbarHeight} />
                        <Switch>
                          <Route path="/candidate/:candidateSlug/interview_requests" component={InterviewRequestsMain} />
                          <Route path="/candidate/:candidateSlug/dashboard" component={DashboardMain} />

                          <Redirect
                            from="/candidate/:candidateSlug/recommended-jobs/:jobId"
                            // Spreading routeProps.location to preserve existing query parameters
                            // while redirecting to a new pathname.
                            to={{
                              ...routeProps.location,
                              pathname: '/candidate/:candidateSlug/jobs/:jobId',
                            }}
                          />
                          <Redirect from="/candidate/:candidateSlug/recommended-jobs" to="/candidate/:candidateSlug/jobs" />
                          <Route path="/candidate/:candidateSlug/jobs" render={({ match }) => <JobsMain match={match} />} />

                          <Route path="/candidate/:candidateSlug/applications" render={() => <ApplicationsMain />} />
                          <Route path="/candidate/:candidateSlug" component={ProfileMain} />
                        </Switch>
                      </ScrollTop>
                    </Body>
                    <If condition={showAdminPanel}>
                      <AdminPanel candidateProfileId={routeProps.match.params.candidateSlug} />
                    </If>
                  </PopulateInitialState>
                </Otherwise>
              </Choose>
            )}
          />
        </Otherwise>
      </Choose>
    </Switch>
  )
}

export default CandidateExperienceMain
